import {
  CloseOutlined,
  RowElement,
  Spacer,
  SpinnerDS,
  TextCapitalized,
  triggerToast,
} from "@qivia/ui";
import {
  ClosedEye,
  OpenEye,
  QiviaLogoBadge,
} from "@qivia/ui/src/assets/assets";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  acknowledgePlatformNotificationAsync,
  getUnacknowledgedPlatformNotificationsAsync,
  getUnacknowledgedPlatformNotificationsCountByTypeAsync,
  selectGetUnacknowledgedPlatformNotificationsCountByTypeStatus,
  notificationsSlice,
  selectAcknowledgePlatformNotificationStatus,
  selectGetUnacknowledgedPlatformNotificationsStatus,
  selectUnacknowledgedPlatformNotificationsCountByType,
  selectUnacknowledgedPlatformNotificationsList,
} from "./notificationsSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCompany } from "../homeSlice";
import { Trans, useTranslation } from "react-i18next";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import {
  fakeNotificationsByCategoriesDisplayedForDemo,
  notificationsCategories,
  notificationsList,
} from "./lib";
import {
  GetUnacknowledgedPlatformNotificationsListType,
  NotificationsCategoriesType,
} from "./notificationsApi";
import "../../../i18n/i18n";

const REFETCH_DEBOUNCE_DELAY_MS = 1000;

export const HistoryTab = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const company = useAppSelector(selectCompany);
  const [categoriesDisplayed, setCategoriesDisplayed] = useState<
    NotificationsCategoriesType[]
  >(() => notificationsCategories);
  const [
    fakeNotificationsDisplayedForDemo,
    setFakeNotificationsDisplayedForDemo,
  ] = useState<GetUnacknowledgedPlatformNotificationsListType[]>(
    fakeNotificationsByCategoriesDisplayedForDemo,
  );

  const unacknowledgedPlatformNotificationsList = useAppSelector(
    selectUnacknowledgedPlatformNotificationsList,
  );
  const getUnacknowledgedPlatformNotificationsStatus = useAppSelector(
    selectGetUnacknowledgedPlatformNotificationsStatus,
  );
  const unacknowledgedPlatformNotificationsCountByType = useAppSelector(
    selectUnacknowledgedPlatformNotificationsCountByType,
  );
  const getUnacknowledgedPlatformNotificationsCountByTypeStatus =
    useAppSelector(
      selectGetUnacknowledgedPlatformNotificationsCountByTypeStatus,
    );
  const acknowledgePlatformNotificationStatus = useAppSelector(
    selectAcknowledgePlatformNotificationStatus,
  );
  const { t } = useTranslation();
  const refetchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  const handleCloseNotification = useCallback(
    (uuid: string) => {
      void dispatch(acknowledgePlatformNotificationAsync(uuid)).then(() => {
        clearTimeout(refetchTimeout.current);
        refetchTimeout.current = setTimeout(() => {
          if (company) {
            void dispatch(
              getUnacknowledgedPlatformNotificationsAsync(company.uuid),
            );
            void dispatch(
              getUnacknowledgedPlatformNotificationsCountByTypeAsync(
                company.uuid,
              ),
            );
          }
        }, REFETCH_DEBOUNCE_DELAY_MS);
      });
    },
    [company, dispatch],
  );

  useEffect(() => {
    return () => clearTimeout(refetchTimeout.current);
  }, []);

  useEffect(() => {
    if (acknowledgePlatformNotificationStatus === "failed") {
      triggerToast(t("notifications.acknowledge.failed"), "error");
      dispatch(
        notificationsSlice.actions.resetAcknowledgePlatformNotificationStatus(),
      );
    }
  }, [acknowledgePlatformNotificationStatus, dispatch, t]);

  const allPlatformNotificationsDisplayed: {
    uuid: string;
    title: string;
    description: string;
    actionPath: string | null;
  }[] = useMemo(() => {
    if (!unacknowledgedPlatformNotificationsList) return [];

    const listDisplayed = isDemoEnvironment()
      ? [
          ...unacknowledgedPlatformNotificationsList,
          ...fakeNotificationsDisplayedForDemo,
        ]
      : unacknowledgedPlatformNotificationsList;

    const filteredNotificationsByCategoriesDisplayed = listDisplayed.filter(
      (notif) => {
        const categoryOfNotification = notificationsList.find(
          (n) => n.type === notif.type,
        )?.category;
        return (
          categoryOfNotification &&
          categoriesDisplayed.includes(categoryOfNotification)
        );
      },
    );

    return filteredNotificationsByCategoriesDisplayed.map((notif) => {
      return {
        uuid: notif.uuid,
        title: notif.title ?? t(`notifications.title.${String(notif.type)}`),
        description: notif.description ?? "",
        actionPath: notif.actionPath ?? null,
      };
    });
  }, [
    categoriesDisplayed,
    fakeNotificationsDisplayedForDemo,
    isDemoEnvironment,
    t,
    unacknowledgedPlatformNotificationsList,
  ]);

  const unacknowledgedPlatformNotificationsCount = useMemo(() => {
    if (!unacknowledgedPlatformNotificationsCountByType) return null;

    const fakeNotificationsTypesForDemo = isDemoEnvironment()
      ? fakeNotificationsDisplayedForDemo.map((notif) => notif.type)
      : [];

    return notificationsList
      .filter((notification) =>
        categoriesDisplayed.includes(notification.category),
      )
      .reduce(
        (total, notification) =>
          total +
          (unacknowledgedPlatformNotificationsCountByType[notification.type] ??
            0) +
          fakeNotificationsTypesForDemo.filter(
            (type) => type === notification.type,
          ).length,
        0,
      );
  }, [
    categoriesDisplayed,
    fakeNotificationsDisplayedForDemo,
    isDemoEnvironment,
    unacknowledgedPlatformNotificationsCountByType,
  ]);

  useEffect(() => {
    if (
      company &&
      getUnacknowledgedPlatformNotificationsCountByTypeStatus === "idle"
    ) {
      void dispatch(
        getUnacknowledgedPlatformNotificationsCountByTypeAsync(company.uuid),
      );
    }
  }, [
    company,
    dispatch,
    getUnacknowledgedPlatformNotificationsCountByTypeStatus,
  ]);

  useEffect(() => {
    if (company && getUnacknowledgedPlatformNotificationsStatus === "idle") {
      void dispatch(getUnacknowledgedPlatformNotificationsAsync(company.uuid));
    }
  }, [company, dispatch, getUnacknowledgedPlatformNotificationsStatus]);

  if (params.tab !== "history") {
    return;
  }

  const isFirstLoading =
    !unacknowledgedPlatformNotificationsList &&
    (getUnacknowledgedPlatformNotificationsStatus === "idle" ||
      getUnacknowledgedPlatformNotificationsStatus === "processing");

  return (
    <StyledFlex>
      <StyledHistory>
        {unacknowledgedPlatformNotificationsCount !== null && (
          <>
            <StyledCount>
              <Trans
                i18nKey="notifications.history.total"
                count={unacknowledgedPlatformNotificationsCount}
              />
            </StyledCount>
            <Spacer y={1} />
          </>
        )}
        {isFirstLoading && (
          <StyledLoader>
            <SpinnerDS />
          </StyledLoader>
        )}
        {unacknowledgedPlatformNotificationsList &&
          allPlatformNotificationsDisplayed.length === 0 && (
            <StyledEmpty>
              <TextCapitalized>
                {t("notifications.history.empty")}
              </TextCapitalized>
            </StyledEmpty>
          )}
        {allPlatformNotificationsDisplayed.map((notif) => (
          <div key={notif.uuid}>
            <RowElement
              key={notif.uuid}
              text={notif.title}
              subText={notif.description}
              leftIcon={<QiviaLogoBadge />}
              borderRadius={0.5}
              size="XL"
              rightElement={
                <StyledClose>
                  <CloseOutlined
                    onClick={(event: React.DragEvent<HTMLDivElement>) => {
                      event.stopPropagation();
                      if (
                        isDemoEnvironment() &&
                        fakeNotificationsDisplayedForDemo.some(
                          (n) => n.uuid === notif.uuid,
                        )
                      ) {
                        setFakeNotificationsDisplayedForDemo(
                          fakeNotificationsDisplayedForDemo.filter(
                            (n) => n.uuid !== notif.uuid,
                          ),
                        );
                        return;
                      }
                      handleCloseNotification(notif.uuid);
                    }}
                  />
                </StyledClose>
              }
              onClickRow={
                notif.actionPath
                  ? () => {
                      notif.actionPath && navigate(notif.actionPath);
                    }
                  : undefined
              }
            />
            <Spacer y={0.5} />
          </div>
        ))}
        {unacknowledgedPlatformNotificationsCount !== null &&
          allPlatformNotificationsDisplayed.length > 0 && (
            <>
              <Spacer y={0.5} />
              <StyledCount>
                <StyledFlex>
                  <Trans
                    i18nKey="notifications.history.displayed"
                    count={allPlatformNotificationsDisplayed.length}
                    values={{ total: unacknowledgedPlatformNotificationsCount }}
                  />
                  <Spacer x={1} />
                  <StyledFooterHint>
                    {t("notifications.history.displayed_hint")}
                  </StyledFooterHint>
                </StyledFlex>
              </StyledCount>
            </>
          )}
      </StyledHistory>
      <Spacer x={2} />
      <Filters
        categoriesDisplayed={categoriesDisplayed}
        setCategoriesDisplayed={setCategoriesDisplayed}
      />
      <Spacer x={1} />
    </StyledFlex>
  );
};

export const Filters = (payload: {
  categoriesDisplayed: NotificationsCategoriesType[];
  setCategoriesDisplayed: (category: NotificationsCategoriesType[]) => void;
}) => {
  const { t } = useTranslation();
  const removeCategory = useCallback(
    (category: NotificationsCategoriesType) => {
      payload.setCategoriesDisplayed(
        payload.categoriesDisplayed.filter((c) => c !== category),
      );
    },
    [payload],
  );

  const addCategory = useCallback(
    (category: NotificationsCategoriesType) => {
      payload.setCategoriesDisplayed([
        ...payload.categoriesDisplayed,
        category,
      ]);
    },
    [payload],
  );

  return (
    <StyledFiltersContainer>
      <TextCapitalized>{t("notifications.history.filters")}</TextCapitalized>
      <Spacer y={1} />
      <StyledFilters>
        {notificationsCategories.map((category) => {
          const isDisplayed = payload.categoriesDisplayed.includes(category);

          return (
            <StyledFilter
              key={category}
              onClick={() => {
                if (isDisplayed) {
                  removeCategory(category);
                } else {
                  addCategory(category);
                }
              }}
            >
              <Spacer y={0.5} />
              <StyledFlex>
                <Spacer x={0.5} />
                {isDisplayed ? <OpenEye /> : <ClosedEye />}
                <Spacer x={0.5} />
                <TextCapitalized>
                  {t(`notifications.category.${category}`)}
                </TextCapitalized>
                <Spacer x={0.5} />
              </StyledFlex>
              <Spacer y={0.5} />
            </StyledFilter>
          );
        })}
      </StyledFilters>
    </StyledFiltersContainer>
  );
};
const StyledFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const StyledFiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 18rem;
  max-width: 18rem;
  ${typographies["Header/H5"]}
`;

const StyledFilter = styled.div`
  display: flex;
  flex-direction: column;
  border: 0.0625rem solid ${colors["colors/borders/input/default"]};
  width: fit-content;
  border-radius: 0.5rem;
  ${typographies["Body/M"]}
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
  & path {
    fill: black;
  }
`;

const StyledClose = styled.div`
  cursor: pointer;
  & svg {
    fill: ${colors["colors/text/lightGrey"]};
  }
`;

const StyledLoader = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledEmpty = styled.div`
  display: flex;
  justify-content: center;
  ${typographies["Body/M"]}
  color: ${colors["colors/text/lightGrey"]};
`;

const StyledCount = styled.div`
  ${typographies["Body/S"]}
`;

const StyledFooterHint = styled.div`
  color: ${colors["colors/text/lightGrey"]};
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledHistory = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 30rem;
`;
